import Vue, { PluginObject } from "vue"; 

const UserType = {
    ANSWERABLE: 1,
    ADMIN: 2,
    STUDENT: 3,
    TEACHER: 4,
    MONITOR: 5,
    COORDINATOR: 6,
    TRANSPORTER: 7,
    GUEST: 8,
    EMPLOYEE: 9,
    MASTER_ADMIN: 99,
};

const rolePermissions: { [key: number]: Array<string | { path: string; methods: string[] }> } = {
    [UserType.MASTER_ADMIN]: ["Home", "Platform", { path: "Tenant", methods: ["GET", "POST", "PATCH", "DELETE"] }, { path: "Role", methods: ["GET", "POST", "PATCH", "DELETE"] }],
    [UserType.ADMIN]: [
        "Home",
        "Manager",
        "Academic",
        { path: "Admin", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Role", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Student", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Employee", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Guest", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Answerable", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "SchoolTransporter", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Equipment", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Occurrence", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Announcement", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "SchoolEnrollment", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "SchoolYear", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "AcademicPeriod", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Education", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Classroom", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Class", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Course", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Assessment", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Attendance", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Calendar", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "SchoolExit", methods: ["GET", "POST", "PATCH"] },
        { path: "SchoolExitSetting", methods: ["GET", "PATCH"] },
    ],
    [UserType.TEACHER]: [
        "Home",
        "Manager",
        "Academic",
        { path: "Student", methods: ["GET"] },
        { path: "Assessment", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Attendance", methods: ["GET", "POST", "PATCH"] },
        { path: "Grade", methods: ["GET", "POST", "PATCH", "DELETE"] },
        { path: "Class", methods: ["GET"] },
        { path: "Course", methods: ["GET"] },
        { path: "AcademicPeriod", methods: ["GET"] },
        { path: "Chat", methods: ["GET", "POST"] },
        { path: "SchoolExit", methods: ["GET", "POST", "PATCH"] },
    ],
    [UserType.MONITOR]: [
        "Home",
        "Manager",
        "Academic",
        { path: "Student", methods: ["GET"] },
        { path: "Attendance", methods: ["GET"] },
        { path: "Occurrence", methods: ["GET", "POST"] },
        { path: "Chat", methods: ["GET", "POST"] },
        { path: "SchoolExit", methods: ["GET", "POST", "PATCH"] },
    ],
};

const profileTypeMap: { [key: string]: number } = {
    master_admin: UserType.MASTER_ADMIN,
    admin: UserType.ADMIN,
    teacher: UserType.TEACHER,
    monitor: UserType.MONITOR,
};

function normalizeUserPayload(payload: any) {
    let data = payload || {};

    for (let index = 0; index < 3; index += 1) {
        const nested = data && data.data;
        const nestedHasUserShape = nested && (
            nested.type ||
            nested.current_profile ||
            nested.profiles ||
            nested.profile_types ||
            nested.profile ||
            nested.role
        );

        if (nestedHasUserShape || (data && !data.type && !data.current_profile && !data.profiles && data.data)) {
            data = data.data;
        }
    }

    return data || {};
}

function readStoredUser() {
    try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        return normalizeUserPayload(stored);
    } catch (error) {
        return {};
    }
}

function resolveUserTypes(rootUser?: any) {
    const user = rootUser && normalizeUserPayload(rootUser);
    const stored = readStoredUser();
    const data = user && (user.type || user.current_profile || user.profiles || user.profile || user.role) ? user : stored;

    const types = [
        data.type,
        data.current_profile && data.current_profile.type,
        ...((data.profiles || []).map((profile: any) => profile.type)),
        ...((data.profile_types || []).map((profileType: string) => profileTypeMap[profileType])),
        data.profile ? UserType.ADMIN : null,
        data.role ? UserType.ADMIN : null,
    ]
        .map((type) => Number(type))
        .filter(Boolean);

    return [...new Set(types)];
}

function resolveUserPermissions(rootUser?: any) {
    const user = rootUser && normalizeUserPayload(rootUser);
    const stored = readStoredUser();
    const data = user && (user.permissions || user.current_profile || user.profiles) ? user : stored;
    const permissions = [
        ...((data.permissions || [])),
        ...((data.current_profile && data.current_profile.permissions) || []),
        ...((data.profiles || []).flatMap((profile: any) => profile.permissions || [])),
    ];

    return permissions;
}

function normalizeMethods(methods: string | string[]) {
    if (Array.isArray(methods)) return methods.map((method) => String(method).toUpperCase());
    return String(methods || "GET")
        .split(",")
        .map((method) => method.trim().toUpperCase())
        .filter(Boolean);
}

function hasPermission(permissions: Array<string | { path: string; methods: string[] }>, path: string, methods: string[]) {
    if (permissions.includes("*")) return true;

    return permissions.some((permission) => {
        if (typeof permission === "string") return permission === path;
        if (permission.path !== path && permission.path !== "*") return false;

        return methods.some((method) => permission.methods.includes("*") || permission.methods.includes(method));
    });
}


class PermissionsPlugin implements PluginObject<any>{

    install = (_Vue: typeof Vue, options?: any) => {
        
        _Vue.prototype.$check = function(path: string, methods: string | string[]){

            if (!path) return true;

            const userPermissions = resolveUserPermissions(this.$root && this.$root.user);
            const userTypes = resolveUserTypes(this.$root && this.$root.user);
            const normalizedMethods = normalizeMethods(methods);
            const basePermissions = userTypes.flatMap((userType) => rolePermissions[userType] || []);
            const effectivePermissions = userPermissions.length ? userPermissions : basePermissions;

            return hasPermission(effectivePermissions, path, normalizedMethods);
            
            // var user = this.$root.user;

            // if(!user) return false;

            // const profile = adminProfiles.find((x) => x.id === (user.data ? user.data.profile : user.profile));

            // if(profile.permissions && profile.permissions[0] === '*') return true;

            // const checkPermission = profile.permissions.find((x) => x.path === path);

            // if(!checkPermission) return false;

            // const checkMethod = checkPermission.method.includes(methods)

            // return checkMethod;

            // return data.permissions.some(perm => {
            //     let regex = new RegExp(perm.path);
            //     return regex.test(path) && methods.some(method => perm.methods.includes(method.toLowerCase()))
            // });
        }  
    };
    [key: string]: any;    
}

const plugin = new PermissionsPlugin;
Vue.use(plugin);
